import { normalizeStateCode, stateLabel } from "./brazilianStates";
import { slugify } from "./slugify";
import { UNGROUPED_GROUP_ID, UNGROUPED_GROUP_NAME, UNGROUPED_GROUP_SLUG, } from "./types";
export function resolveUnitGroups({ units, mode, groups, }) {
    if (!units?.length)
        return [];
    if (!mode || mode === "NONE")
        return [];
    return mode === "STATE"
        ? groupByState(units)
        : groupByConfig({ units, groups });
}
export function buildUnitGroups(params) {
    const withUnits = resolveUnitGroups(params).filter((group) => group.units.length > 0);
    if (!withUnits.length)
        return null;
    if (withUnits.every((group) => group.id === UNGROUPED_GROUP_ID))
        return null;
    return disambiguateSlugs(withUnits);
}
export function findUnitGroup(groups, slugOrId) {
    if (!groups || !slugOrId)
        return null;
    return (groups.find((group) => group.slug === slugOrId) ??
        groups.find((group) => group.id === slugOrId) ??
        null);
}
export function groupSlugsCoveredBy(groups, unitIds) {
    if (!groups?.length)
        return [];
    const applied = new Set(unitIds);
    return groups
        .filter((group) => group.units.length > 0 &&
        group.units.every((unit) => applied.has(unit.id)))
        .map((group) => group.slug);
}
export function unitIdsOfGroup(groups, slugOrId) {
    return findUnitGroup(groups, slugOrId)?.units.map((unit) => unit.id) ?? [];
}
function disambiguateSlugs(groups) {
    const seen = new Map();
    return groups.map((group) => {
        const previous = seen.get(group.slug) ?? 0;
        seen.set(group.slug, previous + 1);
        return previous === 0
            ? group
            : { ...group, slug: `${group.slug}-${previous + 1}` };
    });
}
function groupByState(units) {
    const byState = new Map();
    const withoutState = [];
    for (const unit of units) {
        const code = normalizeStateCode(unit.state);
        if (!code) {
            withoutState.push(unit);
            continue;
        }
        if (!byState.has(code))
            byState.set(code, []);
        byState.get(code).push(unit);
    }
    const groups = [...byState.entries()]
        .map(([code, groupUnits]) => {
        const name = stateLabel(code);
        return { id: code, slug: slugify(name), name, units: groupUnits };
    })
        .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
    return [...groups, ...ungroupedBucket(withoutState)];
}
function groupByConfig({ units, groups, }) {
    if (!groups?.length)
        return [];
    const byId = new Map(units.map((unit) => [unit.id, unit]));
    const grouped = new Set();
    const resolved = groups.map((group) => {
        const groupUnits = [];
        for (const unitId of group.unitIds ?? []) {
            const unit = byId.get(unitId);
            if (!unit || grouped.has(unitId))
                continue;
            grouped.add(unitId);
            groupUnits.push(unit);
        }
        return {
            id: group.id,
            slug: slugify(group.name) || group.id,
            name: group.name,
            units: groupUnits,
        };
    });
    const leftovers = units.filter((unit) => !grouped.has(unit.id));
    return [...resolved, ...ungroupedBucket(leftovers)];
}
function ungroupedBucket(units) {
    if (!units.length)
        return [];
    return [
        {
            id: UNGROUPED_GROUP_ID,
            slug: UNGROUPED_GROUP_SLUG,
            name: UNGROUPED_GROUP_NAME,
            units,
        },
    ];
}
